import { Alert, AlertColor, CircularProgress } from '@mui/material';
import UPSnackBar from 'components/app-container/snackbar/UPSnackbar';
import UPButton from 'components/button';
import { UPTanStackTable } from 'components/tanstack-table';
import { toNumber } from 'lodash';
import {
  LoadErrorRow,
  PolicyIntro,
  SectionBlock,
  SectionTitle,
} from 'modules/management/time-registration-policy/styles';
import { POLICY_SECTIONS, PolicySection, UNIT_DEFINITIONS } from 'modules/management/time-registration-policy/units';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import {
  clearCompanyPolicyAction,
  getCompanyPolicyCoordinatesStatusAction,
  getCompanyTimeRegistrationPolicyAction,
  setCompanyPolicyCoordinatesStatusAction,
} from 'store/actions/company-time-registration-policy-actions';
import {
  companyPolicyAgencyNameSelector,
  companyPolicyCoordinatesStatusSelector,
  companyPolicyLoadErrorSelector,
  companyPolicyLoadingSelector,
  companyPolicyLocationsSelector,
  companyPolicyUnitsSelector,
} from 'store/selectors/company-time-registration-policy-selectors';
import { profileSelector } from 'store/selectors/profile-selectors';
import {
  GENERAL__RETRY,
  TIME_REGISTRATION_POLICY__SAVED,
  TIME_REGISTRATION_SETTINGS__COST_CENTERS_MISSING_COORDINATES,
  TIME_REGISTRATION_SETTINGS__FEATURE_NOT_ENABLED,
  TIME_REGISTRATION_SETTINGS__INTRO,
  TIME_REGISTRATION_SETTINGS__LOAD_ERROR,
} from 'translations/constants';
import { PolicyUnit } from 'types/time-registration-policy/ITimeRegistrationPolicy';
import { buildCompanyPolicyColumns, CompanyPolicyRow, getCompanyPolicyRowName } from './columns';
import CompanyUnitPanel from './CompanyUnitPanel';
import { CompanyPolicyContainer } from './styles';

interface Toast {
  id: number;
  type: AlertColor;
  content: string;
}

const CompanyTimeRegistration = (): JSX.Element => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId } = useParams();
  const numericCompanyId = toNumber(companyId);
  const user = useAppSelector(profileSelector);
  const agencyName = useAppSelector(companyPolicyAgencyNameSelector);
  const units = useAppSelector(companyPolicyUnitsSelector);
  const locations = useAppSelector(companyPolicyLocationsSelector);
  const loading = useAppSelector(companyPolicyLoadingSelector);
  const loadError = useAppSelector(companyPolicyLoadErrorSelector);
  const coordinatesStatus = useAppSelector(companyPolicyCoordinatesStatusSelector);
  const [openUnit, setOpenUnit] = useState<PolicyUnit | null>(null);
  const toastId = useRef(0);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const pushToast = useCallback((type: AlertColor, content: string) => {
    toastId.current += 1;
    const id = toastId.current;
    setToasts(previous => [...previous, { id, type, content }]);
  }, []);
  const dismissToast = (id: number) => setToasts(previous => previous.filter(toast => toast.id !== id));

  const columns = useMemo(() => buildCompanyPolicyColumns(t, user.role, setOpenUnit), [t, user.role]);
  const locationRequired = !!units.find(u => u.unit === PolicyUnit.LocationCapture)?.value.enabled;

  useEffect(() => {
    dispatch(getCompanyTimeRegistrationPolicyAction(numericCompanyId));
    return () => {
      dispatch(clearCompanyPolicyAction());
    };
  }, [dispatch, numericCompanyId]);

  useEffect(() => {
    if (locationRequired) {
      dispatch(getCompanyPolicyCoordinatesStatusAction(numericCompanyId));
    } else {
      dispatch(setCompanyPolicyCoordinatesStatusAction(null));
    }
  }, [dispatch, numericCompanyId, locationRequired]);

  if (loadError === 'featureNotEnabled') {
    return (
      <CompanyPolicyContainer>
        <Alert severity="info" data-testid="tr-company-feature-not-enabled">
          {t(TIME_REGISTRATION_SETTINGS__FEATURE_NOT_ENABLED)}
        </Alert>
      </CompanyPolicyContainer>
    );
  }

  if (loadError === 'failed') {
    return (
      <CompanyPolicyContainer>
        <LoadErrorRow data-testid="tr-company-load-error">
          <Alert severity="error" variant="filled">
            {t(TIME_REGISTRATION_SETTINGS__LOAD_ERROR)}
          </Alert>
          <UPButton
            text={t(GENERAL__RETRY)}
            outlined
            onClick={() => dispatch(getCompanyTimeRegistrationPolicyAction(numericCompanyId))}
            testId="tr-company-load-retry"
          />
        </LoadErrorRow>
      </CompanyPolicyContainer>
    );
  }

  if (loading && !units.length) {
    return (
      <CompanyPolicyContainer>
        <CircularProgress data-testid="tr-company-loading" />
      </CompanyPolicyContainer>
    );
  }

  const openState = units.find(u => u.unit === openUnit);
  const buildSectionRows = (section: PolicySection): CompanyPolicyRow[] =>
    UNIT_DEFINITIONS.filter(def => def.section === section)
      .map(def => ({ def, state: units.find(u => u.unit === def.unit) }))
      .filter(row => !!row.state);

  return (
    <CompanyPolicyContainer data-testid="tr-company">
      <PolicyIntro>{t(TIME_REGISTRATION_SETTINGS__INTRO, { agencyName })}</PolicyIntro>
      {POLICY_SECTIONS.map(({ section, titleKey }) => (
        <SectionBlock key={section} data-testid={`tr-company-section-${section}`}>
          <SectionTitle>{t(titleKey)}</SectionTitle>
          <UPTanStackTable
            data={buildSectionRows(section)}
            columns={columns}
            getRowId={getCompanyPolicyRowName}
            getRowTestId={row => `tr-company-row-${getCompanyPolicyRowName(row)}`}
            testId={`tr-company-table-${section}`}
            enableSorting={false}
            showPaginationControls={false}
            initialPageSize={UNIT_DEFINITIONS.length}
            sx={{
              '& .MuiTableContainer-root': { maxHeight: 'none', overflow: 'visible' },
              '& thead th:last-of-type': { cursor: 'default', '&:hover': { backgroundColor: '#fff' } },
            }}
          />
          {section === 'location' && locationRequired && coordinatesStatus?.hasMissingCoordinates && (
            <Alert severity="warning" sx={{ marginTop: '8px' }} data-testid="tr-company-missing-coordinates">
              {t(TIME_REGISTRATION_SETTINGS__COST_CENTERS_MISSING_COORDINATES)}:{' '}
              {coordinatesStatus.costCentresMissingCoordinates.map(costCentre => costCentre.name).join(', ')}
            </Alert>
          )}
        </SectionBlock>
      ))}
      {openState && (
        <CompanyUnitPanel
          companyId={numericCompanyId}
          state={openState}
          locations={locations}
          role={user.role}
          onClose={() => setOpenUnit(null)}
          onSaved={name => pushToast('success', t(TIME_REGISTRATION_POLICY__SAVED, { name }))}
        />
      )}
      {toasts.map((toast, index) => (
        <UPSnackBar
          key={toast.id}
          open
          type={toast.type}
          content={toast.content}
          onClose={() => dismissToast(toast.id)}
          vertical="bottom"
          horizontal="right"
          index={index}
          testId={`tr-toast-${toast.id}`}
        />
      ))}
    </CompanyPolicyContainer>
  );
};

export default CompanyTimeRegistration;
